import {
  createDefaultMessageEditValues,
  describeMessage,
  describeMessageTarget,
  getMessageValidationMessage,
  messageConfigToEditValues,
  messageEditValuesToConfig,
  normalizeMessageConfig,
  normalizeMessageTarget,
  stringifyMessageConfig,
  type MessageBlockTarget,
  type MessageConfig,
  type MessageEditRequest,
  type MessageEditValues,
} from '#components/document-viewer/message/message-config';

export const NP_MESSAGE_TAG = 'np-message';
export const NP_MESSAGE_OPEN_TAG = `<${NP_MESSAGE_TAG}>`;
export const NP_MESSAGE_CLOSE_TAG = `</${NP_MESSAGE_TAG}>`;

export type MessageBlockData = MessageConfig;

export {
  createDefaultMessageEditValues as createDefaultMarkdownMessageEditValues,
  describeMessage as describeMarkdownMessage,
  describeMessageTarget as describeMarkdownMessageTarget,
  getMessageValidationMessage as getMarkdownMessageValidationMessage,
  messageConfigToEditValues as messageBlockDataToEditValues,
  messageEditValuesToConfig as markdownMessageEditValuesToBlockData,
  normalizeMessageConfig as normalizeMessageBlockData,
  normalizeMessageTarget as normalizeNpMessageTarget,
  stringifyMessageConfig as stringifyMarkdownMessageConfig,
  type MessageEditRequest as MarkdownMessageEditRequest,
  type MessageEditValues as MarkdownMessageEditValues,
  type MessageBlockTarget as NpMessageTarget,
};

export interface ParsedMarkdownMessageConfig {
  data: MessageConfig;
  rawConfig: string;
  parseError: string | null;
}

function normalizeRawConfig(value: string): string {
  return value.replace(/\r\n/g, '\n').trim();
}

function minifyJsonConfig(value: unknown): string {
  return JSON.stringify(value);
}

function messageConfigToSerializable(
  config: MessageConfig,
): Record<string, string> {
  const normalized = normalizeMessageConfig(config);
  if (normalized.target === 'message' && normalized.messageId) {
    return {
      target: 'message',
      messageId: normalized.messageId,
    };
  }

  return { target: normalized.target };
}

export function parseMarkdownMessageConfig(
  rawConfig: string,
): ParsedMarkdownMessageConfig {
  const normalizedRaw = normalizeRawConfig(rawConfig);

  if (!normalizedRaw) {
    return {
      data: normalizeMessageConfig({}),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  }

  try {
    return {
      data: normalizeMessageConfig(JSON.parse(normalizedRaw) as unknown),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalizeMessageConfig({}),
      rawConfig: normalizedRaw,
      parseError:
        error instanceof Error ? error.message : 'Could not parse message JSON',
    };
  }
}

export function serializeMarkdownMessageInnerConfig(params: {
  data: MessageConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return params.parseError && params.rawConfig?.trim()
    ? normalizeRawConfig(params.rawConfig)
    : minifyJsonConfig(messageConfigToSerializable(params.data));
}

export function serializeMarkdownMessageBlock(params: {
  data: MessageConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return `${NP_MESSAGE_OPEN_TAG}${serializeMarkdownMessageInnerConfig(params)}${NP_MESSAGE_CLOSE_TAG}`;
}

export function createDefaultMarkdownMessageValue(
  initialValues?: Partial<MessageEditValues>,
): string {
  return serializeMarkdownMessageBlock({
    data: normalizeMessageConfig({
      target: initialValues?.target ?? 'document',
      messageId: initialValues?.messageId,
    }),
  });
}
